import { useRef } from 'react'
import classNames from 'classnames'

// internal
import { CLUSTER_FORM_MODE } from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'
import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import {
  getBatchesConfig,
  getBatchesPlaceholder,
  getClustersPlaceholder
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/utils'
import { isUserViewerOrGuest } from '@aytm/app/stats/page/question-views/hm/lib/utils'

// components
import { MultiSelect } from 'primereact/multiselect'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { Button } from 'primereact/button'
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import { HelpContent } from '@aytm/app/stats/page/question-views/hm/components/controls/controls-panel/HelpContent'
import { CustomIcon } from '@aytm/app/stats/page/question-views/hm/components/controls/controls-panel/CustomIcon'

// styles
import style from '@aytm/app/stats/page/question-views/hm/components/controls/controls-panel/styles/ControlsPanel.module.scss'

export const ControlsPanel = ({
  id,
  form,
  userRoles,
  batches,
  config,
  clusters,
  onChangeModel,
  changeClustersForm
}) => {
  const isViewerOrGuest = isUserViewerOrGuest(userRoles)
  const pinTypesRef = useRef(null)
  const clustersRef = useRef(null)

  // Each control names the thing it changes. Reading the key off the event instead ties every one of
  // them to a `name` prop that looks decorative, and a control that loses it goes on firing while
  // writing under no key at all -- the panel answers the hand and changes nothing.
  const handleChangeFilter = event => {
    onChangeModel({ viewMode: event.value })
  }

  // A pick reloads the block under its loading veil, and the list is drawn inline, so an open list
  // would sit under the image until the reload ends. It closes on the pick, as master's did.
  const handleChangePinTypes = event => {
    pinTypesRef.current?.hide()
    onChangeModel({ config: getBatchesConfig({ value: event.value, batches, config }) })
  }

  const handleChangeClusters = event => {
    onChangeModel({
      clusters: clusters.map(cluster => ({ ...cluster, off: !event.value.includes(cluster.uid) }))
    })
  }

  // Taken as a state to move to rather than read off the event: the box reports its own `checked`,
  // but the label beside it answers a plain click, which carries no such thing.
  const setClustersMode = on => onChangeModel({ clustersMode: on ? 'clusters-on' : 'clusters-off' })

  const setMaskToSelected = on => onChangeModel({ config: { ...config, applyClustersMask: on } })

  const handleClustersModeChange = event => setClustersMode(event.checked)

  const handleMaskSelectedChange = event => setMaskToSelected(event.checked)

  const clusterItemTemplate = option => {
    const cluster = clusters.find(c => c.uid === option.value)

    return (
      <div className={style.clusterItem}>
        <span className={style.clusterItemLabel}>{option.text}</span>
        {!isViewerOrGuest &&
          (cluster?.editor ? (
            <i
              className={classNames('icon-aytmfd-lock', style.clusterItemIcon, style.clusterItemLock)}
              title="Clusters created in the Survey Editor are not editable during analysis."
            />
          ) : (
            <i
              className={classNames('icon-aytmfd-pencil', style.clusterItemIcon)}
              onClick={e => {
                e.stopPropagation()
                // the edit form opens over the chart, and the list would otherwise stay open above it
                clustersRef.current?.hide()
                changeClustersForm(CLUSTER_FORM_MODE.EDIT, option.value)
              }}
            />
          ))}
      </div>
    )
  }

  const clusterFooterTemplate = () => {
    const { pins, respondents, comments } = form.clusters.stat
    const count = form.clusters.value.length
    if (!count) return null

    return (
      <div className={style.clusterFooter}>
        <strong>
          Selected {pluralize(count, 'cluster')} {1 == count ? 'contains' : 'contain'}:
        </strong>
        <div className={style.clusterFooterContent}>
          <b>{pins}</b> {pluralize(pins, 'pin')} from <b>{respondents}</b> {pluralize(respondents, 'respondent')}{' '}
          <b>{comments}</b> {pluralize(comments, 'comment')}
        </div>
      </div>
    )
  }

  return (
    <div className={classNames(style.controls, { [style.controlsGuest]: isViewerOrGuest })}>
      <div className={style.controlsGroup}>
        {form.viewMode.isEnabled && (
          <Dropdown
            // every view mode fits, so the list draws no scrollbar (the library caps it at 200px)
            className={classNames(style.select, 'p-dropdown-rounded', 'p-dropdown-no-scroll', {
              [style.selectWide]: isViewerOrGuest
            })}
            aria-label="View mode"
            ariaLabel="View mode"
            value={form.viewMode.value}
            options={form.viewMode.options}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            disabled={form.isLoading}
            valueTemplate={option => (
              <>
                View Mode: <b>{option.text}</b>
              </>
            )}
            onChange={form.isLoading ? null : handleChangeFilter}
          />
        )}

        {form.filterPinTypes.isEnabled && (
          <MultiSelect
            ref={pinTypesRef}
            data-testid="stat-heatmap-pin-types"
            className={classNames(style.select, 'p-multiselect-rounded', {
              [style.selectWide]: isViewerOrGuest
            })}
            placeholder={getBatchesPlaceholder(form.filterPinTypes.value, form.filterPinTypes.options)}
            value={form.filterPinTypes.value}
            options={form.filterPinTypes.options}
            optionValue="value"
            optionLabel="text"
            filterPlaceholder="Search items"
            removeIcon={null}
            fixedPlaceholder={true}
            filter
            resetFilterOnHide
            disabled={form.isLoading}
            // a survey can carry many pin types; the shared header draws the search field alone
            panelHeaderTemplate={MultiSelectHeader}
            onChange={form.isLoading ? null : handleChangePinTypes}
          />
        )}
      </div>

      {form.clusters.isEnabled && (
        <div className={style.filterClusters}>
          {form.clusters.showControls && (
            <div className={style.filterClustersControls}>
              <div className={style.filterClusterCheckboxes}>
                <div className={style.checkbox}>
                  <Checkbox
                    className="p-checkbox-sm"
                    aria-labelledby={`${id}-clusters-mode-label`}
                    inputId={`${id}-clusters-mode`}
                    checked={form.clusters.clustersMode}
                    disabled={form.isLoading}
                    onChange={form.isLoading ? null : handleClustersModeChange}
                  />
                  <span
                    id={`${id}-clusters-mode-label`}
                    className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': form.isLoading })}
                    onClick={form.isLoading ? null : () => setClustersMode(!form.clusters.clustersMode)}
                  >
                    Show Clusters
                  </span>
                </div>
                <div className={style.checkbox}>
                  <Checkbox
                    className="p-checkbox-sm"
                    aria-labelledby={`${id}-mask-selected-label`}
                    inputId={`${id}-mask-selected`}
                    checked={form.clusters.maskToSelected}
                    disabled={form.isLoading}
                    onChange={form.isLoading ? null : handleMaskSelectedChange}
                  />
                  <span
                    id={`${id}-mask-selected-label`}
                    className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': form.isLoading })}
                    onClick={form.isLoading ? null : () => setMaskToSelected(!form.clusters.maskToSelected)}
                  >
                    Mask to selected
                  </span>
                </div>
              </div>
              <div>
                <MultiSelect
                  name="clusters"
                  ref={clustersRef}
                  className={classNames(style.selectClusters, 'p-multiselect-rounded', {
                    [style.selectClustersWide]: isViewerOrGuest
                  })}
                  placeholder={getClustersPlaceholder(form.clusters.value, form.clusters.options)}
                  value={form.clusters.value}
                  options={form.clusters.options}
                  optionValue="value"
                  optionLabel="text"
                  filterPlaceholder="Search items"
                  removeIcon={null}
                  fixedPlaceholder={true}
                  filter
                  resetFilterOnHide
                  disabled={form.isLoading || !form.clusters.options.length}
                  itemTemplate={clusterItemTemplate}
                  panelHeaderTemplate={MultiSelectHeader}
                  panelFooterTemplate={clusterFooterTemplate}
                  onChange={form.isLoading || !form.clusters.options.length ? null : handleChangeClusters}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {form.clusters.isEnabled && !isViewerOrGuest && (
        <div className={style.filterClustersCreate}>
          <div className={classNames(style.label, style.filterClustersLabel)}>
            Add clusters
            <Popup
              width="400px"
              trigger="click"
              direction="bottomright"
              popupOffset={9}
              hideOtherPopupsOnShow
              content={() => <HelpContent />}
            >
              <i className="icon-aytmfd-help-v2" />
            </Popup>
          </div>
          <div className={classNames('p-buttonset p-buttonset-fill', style.filterClustersButtons)}>
            <Button
              className="p-button-rounded p-button-blue"
              disabled={form.isLoading}
              onClick={form.isLoading ? null : () => changeClustersForm(CLUSTER_FORM_MODE.RECOGNITION)}
            >
              <i className="icon-aytmfd-bots" />
              Auto
            </Button>
            <Button
              className="p-button-rounded p-button-blue"
              disabled={form.isLoading}
              onClick={form.isLoading ? null : () => changeClustersForm(CLUSTER_FORM_MODE.ADD)}
            >
              <CustomIcon /> Custom
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
